import {
    AuthenticationDetails,
    CognitoUser,
    CognitoUserPool,
} from 'amazon-cognito-identity-js';
import {
    apiUrl,
    getIdToken as apiGetIdToken,
    loginSchoolshub as apiLoginSchoolshub,
    setRefreshToken as apiSetRefreshToken,
} from './api';

const CUSTOM_EMAIL_SENDER_CLTP_APP = 'cltp';
const CUSTOM_EMAIL_SENDER_RESET_PASSWORD_ACTION = 'resetPassword';

const isDev = 'development' === process.env.NODE_ENV;

// Legacy key only — cleared on logout so older clients do not leave a JS-readable refresh token behind
const LOCAL_STORAGE_REFRESH_TOKEN_KEY = 'cltp-refresh-token';
const LOCAL_STORAGE_LAST_USED_EMAIL_KEY = 'cltp-last-used-email';
const REQUEST_REFRESH_TOKEN_KEY = 'refreshToken';
const REQUEST_ACCESS_TOKEN_KEY = 'accessToken';
const RESPONSE_ID_TOKEN_KEY = 'idToken';
const RESPONSE_EMAIL_KEY = 'email';

const userPool = new CognitoUserPool({
    UserPoolId: '' !== process.env.REACT_APP_COGNITO_USER_POOL_ID ? process.env.REACT_APP_COGNITO_USER_POOL_ID : 'eu-west-2_7B93hl4b3', // Will be replaced by sed during build
    ClientId: '' !== process.env.REACT_APP_COGNITO_CLIENT_ID ? process.env.REACT_APP_COGNITO_CLIENT_ID : '6k0tu49gkk293mf435hgp95bbj', // Will be replaced by sed during build
});

let cognitoUser;

const cognitoUserFromEmail = (email) => cognitoUser = new CognitoUser({
    Username: email,
    Pool: userPool,
});

const getCognitoUser = () => {
    return cognitoUser ?? cognitoUserFromEmail(getLastUsedEmail());
};
const clearCognitoUser = () => cognitoUser = undefined;

export const login = (email, password) => new Promise((resolve, reject) => {
    const cognitoUser = cognitoUserFromEmail(email);

    cognitoUser.authenticateUser(new AuthenticationDetails({
        Username: email,
        Password: password,
    }), {
        onSuccess: (result) => {
            setLastUsedEmail(email);
            localStorage.removeItem(LOCAL_STORAGE_REFRESH_TOKEN_KEY);

            const idToken = result.getIdToken().getJwtToken();
            const refreshToken = result.getRefreshToken().getToken();
            const accessToken = result.getAccessToken().getJwtToken();

            // Persist refresh token in backend HttpOnly sessions only — never localStorage
            apiSetRefreshToken({
                token: idToken,
                data: {
                    [REQUEST_REFRESH_TOKEN_KEY]: refreshToken,
                },
            });

            apiLoginSchoolshub({
                token: idToken,
                data: {
                    [REQUEST_REFRESH_TOKEN_KEY]: refreshToken,
                    [REQUEST_ACCESS_TOKEN_KEY]: accessToken,
                },
            });

            return resolve(idToken);
        },
        onFailure: (error) => {
            isDev && console.error(error);
            return reject(error);
        },
    });
});

export const refreshToken = () => new Promise((resolve, reject) => {
    apiGetIdToken({})
        .then((response) => {
            if (401 === response.status || !response.data?.[RESPONSE_ID_TOKEN_KEY]) {
                return reject(new Error('No refresh token found'));
            }

            setLastUsedEmail(response.data[RESPONSE_EMAIL_KEY]);
            return resolve(response.data[RESPONSE_ID_TOKEN_KEY]);
        })
        .catch((error) => {
            isDev && console.error(error);
            return reject(error);
        });
});

export const forgotPassword = (email) => new Promise((resolve, reject) => {
    const cognitoUser = cognitoUserFromEmail(email);

    cognitoUser.forgotPassword(
        {
            onSuccess: (data) => {
                return resolve(data);
            },
            onFailure: (error) => {
                isDev && console.error(error);
                return reject(error);
            },
        },
        {
            app: CUSTOM_EMAIL_SENDER_CLTP_APP,
            action: CUSTOM_EMAIL_SENDER_RESET_PASSWORD_ACTION,
        },
    );
});

export const resetPassword = (email, newPassword, code) => new Promise((resolve, reject) => {
    const cognitoUser = cognitoUserFromEmail(email);

    cognitoUser.confirmPassword(code, newPassword, {
        onSuccess: (success) => {
            return resolve(success);
        },
        onFailure: (error) => {
            isDev && console.error(error);
            return reject(error);
        },
    });
});

export const logout = () => {
    const finishLogout = () => {
        clearCognitoUser();
        clearLastUsedEmail();
        localStorage.removeItem(LOCAL_STORAGE_REFRESH_TOKEN_KEY);
        window.location.href = apiUrl + '/logout';
    };

    const cognitoUser = getCognitoUser();

    cognitoUser.globalSignOut({
        onSuccess: finishLogout,
        onFailure: (error) => {
            isDev && console.error(error);
            finishLogout();
        },
    });
};

const setLastUsedEmail = (lastUsedEmail) => localStorage.setItem(LOCAL_STORAGE_LAST_USED_EMAIL_KEY, lastUsedEmail);
const getLastUsedEmail = () => localStorage.getItem(LOCAL_STORAGE_LAST_USED_EMAIL_KEY);
const clearLastUsedEmail = () => localStorage.setItem(LOCAL_STORAGE_LAST_USED_EMAIL_KEY, undefined);
